import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sun, Loader2, CheckCircle, Building2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('SalesRep');
  const [organizationSubdomain, setOrganizationSubdomain] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [registered, setRegistered] = useState(false);
  const [registeredOrg, setRegisteredOrg] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await api.get('/organizations/list');
      setOrganizations(response.data.organizations || []);
    } catch (error) {
      toast.error('Failed to load organizations');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!organizationSubdomain) {
      toast.error('Please select an organization to join');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.post('/auth/register', {
        name,
        email,
        password,
        role,
        organization_subdomain: organizationSubdomain
      });
      
      const org = organizations.find(o => o.subdomain === organizationSubdomain);
      setRegisteredOrg(org?.name || organizationSubdomain);
      setRegistered(true);
      toast.success('Registration submitted! Please wait for approval.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-amber-500/5 pointer-events-none" />
        
        <Card className="w-full max-w-md border-slate-200 dark:border-slate-800 shadow-xl animate-fade-in">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white mb-2">
              Registration Submitted!
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Your request to join <strong className="text-emerald-600">{registeredOrg}</strong> has been submitted.
            </p>
            <p className="text-slate-500 text-sm mb-6">
              The managers and administrators of this organization have been notified and will review your request.
              You&apos;ll receive an email once your account is approved.
            </p>
            <Link to="/login">
              <Button variant="outline" className="mt-2" data-testid="back-to-login-btn">
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-amber-500/5 pointer-events-none" />
      
      <div className="w-full max-w-md relative animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-600 shadow-lg shadow-emerald-600/30 mb-4">
            <Sun className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            SolarSales CRM
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            Join your organization
          </p>
        </div>

        <Card className="border-slate-200 dark:border-slate-800 shadow-xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-heading font-bold text-center">
              Request to Join
            </CardTitle>
            <CardDescription className="text-center">
              Select your organization and create your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Organization Selection */}
              <div className="space-y-2">
                <Label htmlFor="organization" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-emerald-500" />
                  Organization *
                </Label>
                {loadingOrgs ? (
                  <div className="flex items-center justify-center h-11 border rounded-md">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  </div>
                ) : organizations.length === 0 ? (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">No organizations available for registration</span>
                    </div>
                  </div>
                ) : (
                  <Select value={organizationSubdomain} onValueChange={setOrganizationSubdomain}>
                    <SelectTrigger className="h-11" data-testid="register-org-select">
                      <SelectValue placeholder="Select your organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map((org) => (
                        <SelectItem key={org.id} value={org.subdomain}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="h-11"
                  data-testid="register-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="h-11"
                  data-testid="register-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="h-11" data-testid="register-role-select">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SalesRep">Sales Representative</SelectItem>
                    <SelectItem value="BackOffice">Back Office</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-lg shadow-emerald-600/20"
                disabled={loading || loadingOrgs || organizations.length === 0}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Request to Join'
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm">
              <span className="text-slate-600 dark:text-slate-400">
                Already have an account?{' '}
              </span>
              <Link
                to="/login"
                className="text-emerald-600 hover:text-emerald-700 font-medium"
                data-testid="login-link"
              >
                Sign in here
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Info box */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>How it works:</strong> After you submit, your organization&apos;s managers and administrators will be notified to approve your request. You&apos;ll get an email once approved.
          </p>
        </div>

        {/* Tenant Signup Link */}
        <div className="mt-4 text-center">
          <p className="text-sm text-slate-500">
            Want to create a new organization?{' '}
            <Link to="/signup" className="text-emerald-600 hover:underline font-medium">
              Sign up as a tenant
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
